import { useContactAdsQuery } from '@/hooks/queries/use-contact-ads-query';
import {
  MentionBoostAuthorizationScope,
  type ContactAdDto,
  type ListContactAdsResponse,
} from '@fluenceur/types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { ContactBoost } from '../views/ContactBoostRow';
import { useResolvedContactIdentityId } from '../use-resolved-contact-identity-id';

export interface ActiveContactAds {
  ads: ContactBoost[];
  /**
   * Whether the signed-in user's mailbox can reach THIS contact, and why not
   * when it cannot. It depends on the user and the contact, never on the
   * individual authorization, so it travels once beside the list.
   */
  emailChannel: ListContactAdsResponse['emailChannel'];
  isLoading: boolean;
}

const UNREACHABLE: ListContactAdsResponse['emailChannel'] = { available: false, blocker: null };

const EMPTY_FALLBACK: ActiveContactAds = {
  ads: [],
  emailChannel: UNREACHABLE,
  isLoading: false,
};

/**
 * Max characters for a row title before it is ellipsised. A display concern the
 * dashboard owns: the API returns the full caption, the row truncates it.
 */
const TITLE_MAX_LENGTH = 60;

/** Truncate a title to the row width, appending an ellipsis when shortened. */
const truncateTitle = (title: string): string =>
  title.length > TITLE_MAX_LENGTH ? `${title.slice(0, TITLE_MAX_LENGTH).trimEnd()}...` : title;

/**
 * What the authorization is about, in three cases.
 *
 * A content-level authorization on a post with no caption used to fall through
 * to the account title, saying the creator had granted far more than they had.
 * The scope now decides, and the caption only names.
 */
const toTitle = (dto: ContactAdDto, t: TFunction): string => {
  if (dto.scope === MentionBoostAuthorizationScope.ACCOUNT_LEVEL) {
    return t('contactDrawer.ads.accountLevel');
  }

  return dto.contentTitle
    ? truncateTitle(dto.contentTitle)
    : t('contactDrawer.ads.contentFallback');
};

/**
 * One line of context that never repeats the status badge.
 *
 * The badge already says where Meta stands, so restating "authorized" under it
 * was the same fact twice. What the badge cannot say is whether an ad is
 * actually running, and what the grant covers — so that is what this says.
 */
const toSublabel = (dto: ContactAdDto, t: TFunction): string => {
  if (dto.hasLiveBoost) {
    return t('contactDrawer.ads.sublabel.delivering');
  }

  return dto.scope === MentionBoostAuthorizationScope.CONTENT_LEVEL
    ? t('contactDrawer.ads.sublabel.contentScope')
    : t('contactDrawer.ads.sublabel.accountScope');
};

/**
 * The DTO plus the two strings the dashboard derives from it. Spread rather than
 * copied field by field: every field the API adds arrives here for free, and
 * nobody has to remember to forward it.
 */
const toContactBoost = (dto: ContactAdDto, t: TFunction): ContactBoost => ({
  ...dto,
  title: toTitle(dto, t),
  sublabel: toSublabel(dto, t),
});

/**
 * Resolves the active partnership-ads slice for the PUBLICITÉ tab.
 *
 * Reads the real, brand-scoped authorizations list (lazy, keyed by the resolved
 * `creatorIdentityId`). When the identity is unresolved, returns an empty slice
 * so the view shows its informational empty state.
 *
 * The list is also the detail's only data source: opening one authorization
 * costs no extra request. `enabled` lets a caller that only wants to READ the
 * cache (the context bar's title) avoid firing a request of its own.
 */
export const useActiveContactAds = (enabled = true): ActiveContactAds => {
  const { t } = useTranslation();
  const creatorIdentityId = useResolvedContactIdentityId();

  const { data, isLoading } = useContactAdsQuery(creatorIdentityId, enabled);

  const ads = useMemo(
    () => (data?.data ?? []).map((dto) => toContactBoost(dto, t)),
    [data?.data, t]
  );

  if (!creatorIdentityId) {
    return EMPTY_FALLBACK;
  }

  return {
    ads,
    emailChannel: data?.emailChannel ?? UNREACHABLE,
    isLoading,
  };
};
